import React, { useEffect, useId, useMemo, useState } from 'react'
import { ChartsAxisContentProps, LineChart } from '@mui/x-charts'
import { Theme, useTheme } from '@mui/material/styles'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import { firebase_functions } from '../../common/firebaseConfig'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import Box from '@mui/material/Box'
import { Typography } from '@mui/material'
import { ChartSeriesDefaultized } from '@mui/x-charts/models/seriesType/config'
import Divider from '@mui/material/Divider'
import {
  formatCompactCurrency,
  formatMonthYear,
  formatPercentage,
  toDrawdownPercentages,
} from '../../common/utils/backtestSummaryStats'

interface TradeSummary {
  range: Date
  profit: number
  // Per-bucket intra-period stats from getBacktestTradeProfits (absent on older cached responses).
  high?: number
  low?: number
  maxDrawdown?: number
  maxDrawdownPercentage?: number
  // Account value including open trades: at the bucket's end, and its low/high since the previous bucket.
  // With these, maxDrawdownPercentage is open-trade aware too (backtests only; absent when price data is missing).
  equity?: number
  equityLow?: number
  equityHigh?: number
  // Client-derived: equity − the group's starting equity, i.e. net profit including open trades.
  netEquity?: number
}

interface GroupData {
  group: string
  startingEquity?: number
  data: TradeSummary[]
}

interface ChartDataPoint {
  x: Date
  y: number
}

interface ChartDataSeries {
  id?: string
  data: number[] // Y-values
  label: string
  type: 'line'
  showMark?: boolean
  color?: string
  lineStyle?: { strokeDasharray: string }
  area?: boolean
  disableHighlight?: boolean
  curve?: 'linear' | 'monotoneX' | 'monotoneY' | 'natural' | 'step' | 'stepBefore' | 'stepAfter'
  valueFormatter?: (value: null | number) => string
}

function BacktestEquityChart(props: {
  backtestId?: string
  alertId?: string
  tradeGroups: string[]
  accountEquity: number
  // Card presentation: the highlighted ratio is drawn bold with a fill over faint peers,
  // with compact axes and a drawdown strip underneath.
  highlightGroup?: string
  // Highlight whichever loaded group ends most profitable (for callers that don't know group names).
  highlightTopGroup?: boolean
}) {
  const { tradeGroups, backtestId, alertId, accountEquity, highlightGroup, highlightTopGroup } =
    props
  const theme = useTheme()
  // Per-instance: the same backtest can be charted twice at once (its card behind the backtest view),
  // and url(#id) resolves to the first matching gradient in the document.
  const gradientId = `equity-fill-${useId().replace(/[^\w-]/g, '')}`
  const { usermeta } = useApplicationSuiteContext()

  const [groupProfits, setGroupProfits] = useState<Record<string, TradeSummary[]>>({})
  const [yAxisRange, setYAxisRange] = useState<{ min: number; max: number } | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const [getBacktestTradeProfits] = useHttpsCallable(firebase_functions, 'getBacktestTradeProfits')

  // Groups bucket profits by exit date, so each group can add dates the others lack. Every series
  // is re-aligned to the shared axis whenever the loaded groups change.
  const xAxisDates = useMemo(() => buildDateAxis(Object.values(groupProfits)), [groupProfits])
  const chartGroupsData = useMemo(
    () =>
      buildSeriesByGroup(groupProfits, xAxisDates, {
        idPrefix: `${backtestId || alertId}`,
        accountEquity,
      }),
    [groupProfits, xAxisDates, backtestId, alertId, accountEquity]
  )

  const fetchChartDataForGroups = async (groupsToFetch: string[]) => {
    if (!alertId && !backtestId) return
    if (backtestId && groupsToFetch.length === 0) return

    setIsLoading(true)
    const queryParams = backtestId ? { backtestId, groups: groupsToFetch } : { alertId }
    try {
      // useHttpsCallable resolves undefined on failure, and an unauthenticated call returns { status }.
      const result: any = await getBacktestTradeProfits(queryParams)
      const groupTradeProfits: GroupData[] | undefined = result?.data
      if (!Array.isArray(groupTradeProfits)) throw new Error('getBacktestTradeProfits failed')

      setLoadError(false)
      setGroupProfits((prev) => {
        const updated = { ...prev }
        for (const groupData of groupTradeProfits) {
          updated[groupData.group] = withNetEquity(groupData)
        }
        return updated
      })
    } catch (error) {
      console.error('Error loading trade profits:', error)
      setLoadError(true)
    } finally {
      setIsLoading(false)
    }
  }

  // On initial mount or whenever tradeGroups changes, fetch or remove series
  useEffect(() => {
    if ((!backtestId && !alertId) || !usermeta) return

    // For alerts, fetch data immediately without requiring trade groups
    if (alertId && Object.keys(groupProfits).length === 0) {
      fetchChartDataForGroups([])
      return
    }

    // For backtests, handle trade groups logic
    if (backtestId) {
      const currentGroups = Object.keys(groupProfits)

      // Identify new groups to fetch
      const newGroups = tradeGroups.filter((g) => !currentGroups.includes(g))

      // Identify groups to remove
      const removedGroups = currentGroups.filter((g) => !tradeGroups.includes(g))

      if (newGroups.length > 0) {
        fetchChartDataForGroups(newGroups)
      }

      if (removedGroups.length > 0) {
        setGroupProfits((prev) => {
          const updated = { ...prev }
          for (const rg of removedGroups) {
            delete updated[rg]
          }
          return updated
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeGroups, backtestId, alertId, usermeta])

  // Recalculate Y axis range whenever the plotted series change
  useEffect(() => {
    if (alertId) {
      // For alerts, use all available data
      const allSeries = Object.values(chartGroupsData)
      if (allSeries.length > 0) {
        recalculateYAxisRange([], chartGroupsData)
      }
    } else if (tradeGroups.length > 0) {
      // For backtests, use only the specified trade groups
      recalculateYAxisRange(tradeGroups, chartGroupsData)
    }
  }, [chartGroupsData, tradeGroups, alertId])

  const recalculateYAxisRange = (
    activeGroups: string[],
    groupData: Record<string, ChartDataSeries>
  ) => {
    // If activeGroups is empty, use all available series (for alerts)
    const activeSeries =
      activeGroups.length === 0
        ? Object.values(groupData)
        : activeGroups.map((g) => groupData[g]).filter(Boolean)

    if (activeSeries.length === 0) {
      setYAxisRange(null)
      return
    }

    const allYValues = activeSeries.flatMap((serie) => serie.data)
    const minY = Math.min(...allYValues)
    const maxY = Math.max(...allYValues)
    setYAxisRange({ min: minY + minY * 0.1, max: maxY })
  }

  // Build the final chartData array including baseline
  // Duplicate trade groups (e.g. from duplicated backend summaries) would otherwise
  // produce two series with the same MUI X series id and crash the chart.
  const sortedGroups = (
    alertId
      ? Object.values(chartGroupsData)
      : Array.from(new Set(tradeGroups))
          .map((g) => chartGroupsData[g])
          .filter(Boolean)
  ).sort((a, b) => b.data[b.data.length - 1] - a.data[a.data.length - 1])

  // Baseline series
  const baselineSeries: ChartDataSeries = {
    id: `${backtestId || alertId}-baseline`,
    data: xAxisDates.map(() => 0),
    label: '',
    type: 'line',
    showMark: false,
    color: 'lightgray',
    lineStyle: { strokeDasharray: '5,5' },
    disableHighlight: true,
  }

  const highlightKey =
    highlightGroup ?? (highlightTopGroup ? getTopGroup(chartGroupsData) : undefined)
  const highlightedSeries = highlightKey ? chartGroupsData[highlightKey] : undefined
  const highlightColor = getHighlightColor(highlightedSeries, theme)
  const drawdown = useMemo(
    () =>
      highlightKey && highlightedSeries
        ? buildDrawdownSeries(
            groupProfits[highlightKey] ?? [],
            xAxisDates,
            highlightedSeries.data,
            accountEquity
          )
        : null,
    [highlightKey, highlightedSeries, groupProfits, xAxisDates, accountEquity]
  )
  const AxisContent = useMemo(() => {
    function AxisContentWithDrawdown(axisProps: ChartsAxisContentProps) {
      return (
        <RenderAxisContent
          {...axisProps}
          drawdown={drawdown ?? undefined}
          drawdownLabel={highlightKey}
        />
      )
    }
    return AxisContentWithDrawdown
  }, [drawdown, highlightKey])
  const finalChartData = highlightKey
    ? [...styleForHighlight(sortedGroups, highlightedSeries, highlightColor), baselineSeries]
    : [...sortedGroups, baselineSeries]

  if (finalChartData.length === 0) {
    return null
  }

  if (loadError && Object.keys(groupProfits).length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="caption" color="text.secondary">
          Couldn't load performance data
        </Typography>
      </Box>
    )
  }

  const lineChart = (
    <LineChart
      loading={isLoading}
      skipAnimation
      xAxis={[
        {
          scaleType: 'time',
          data: xAxisDates,
          valueFormatter: (value: Date, context) => {
            if (context.location === 'tooltip') {
              return `Net profit ${value.toLocaleDateString()}`
            }
            if (highlightKey) {
              return formatMonthYear(value)
            }
            return `${value.toLocaleDateString()}`
          },
          min: xAxisDates[0],
          max: xAxisDates[xAxisDates.length - 1],
        },
      ]}
      yAxis={[
        {
          valueFormatter: (value: number) =>
            highlightKey ? formatCompactCurrency(value) : `$${value.toFixed(0)}`,
          min: yAxisRange?.min,
          max: yAxisRange?.max,
        },
      ]}
      slots={{
        axisContent: AxisContent,
      }}
      series={finalChartData}
      margin={{ top: 8, right: 10, left: highlightKey ? 44 : 50, bottom: 24 }}
      slotProps={{
        legend: { hidden: true },
      }}
      sx={
        highlightKey
          ? {
              '& .MuiAreaElement-root': { fill: `url(#${gradientId})` },
              '& .MuiChartsAxis-tick, & .MuiChartsAxis-line': { stroke: theme.palette.divider },
              '& .MuiChartsAxis-tickLabel': { fill: theme.palette.text.secondary },
            }
          : undefined
      }
    >
      {highlightKey && (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={highlightColor} stopOpacity={0.3} />
            <stop offset="1" stopColor={highlightColor} stopOpacity={0} />
          </linearGradient>
        </defs>
      )}
    </LineChart>
  )

  if (!highlightKey) {
    return lineChart
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>{lineChart}</Box>
      {highlightedSeries && (
        <DrawdownStrip
          id={`${backtestId || alertId}-drawdown`}
          data={drawdown ?? []}
          dates={xAxisDates}
        />
      )}
    </Box>
  )
}

function getTopGroup(seriesByGroup: Record<string, ChartDataSeries>): string | undefined {
  let top: string | undefined
  let topValue = -Infinity
  for (const [group, series] of Object.entries(seriesByGroup)) {
    const last = series.data[series.data.length - 1] ?? 0
    if (last > topValue) {
      top = group
      topValue = last
    }
  }
  return top
}

// Uses each bucket's own deepest drawdown so dips that recover inside a bucket still show; falls back
// to the end-of-bucket curve when the response predates those fields.
function buildDrawdownSeries(
  buckets: TradeSummary[],
  dates: Date[],
  cumulativeProfit: number[],
  accountEquity: number
): number[] {
  if (hasOpenTradeEquity(buckets)) {
    return buildOpenTradeDrawdownSeries(buckets, dates)
  }

  const hasBucketStats = buckets.every(
    (bucket) => bucket.maxDrawdownPercentage != null && bucket.high != null
  )
  if (!buckets.length || !hasBucketStats) {
    return toDrawdownPercentages(cumulativeProfit, accountEquity)
  }

  const bucketByDay = new Map(buckets.map((bucket) => [toDay(bucket.range), bucket]))
  let peak = 0
  return dates.map((date, index) => {
    const bucket = bucketByDay.get(date.getTime())
    if (bucket) {
      peak = Math.max(peak, bucket.high as number)
      return -(bucket.maxDrawdownPercentage as number)
    }
    const peakEquity = accountEquity + peak
    return peakEquity > 0 ? ((cumulativeProfit[index] - peak) / peakEquity) * 100 : 0
  })
}

// Open-trade-aware strip: bucket dates use the bucket's deepest drop; dates only other groups have
// hold this group's last account value against its running peak.
function buildOpenTradeDrawdownSeries(buckets: TradeSummary[], dates: Date[]): number[] {
  const bucketByDay = new Map(buckets.map((bucket) => [toDay(bucket.range), bucket]))
  let peak = 0
  let equity: number | undefined
  return dates.map((date) => {
    const bucket = bucketByDay.get(date.getTime())
    if (bucket) {
      peak = Math.max(peak, bucket.equityHigh as number)
      equity = bucket.equity as number
      return -(bucket.maxDrawdownPercentage as number)
    }
    return equity !== undefined && peak > 0 ? Math.min(0, ((equity - peak) / peak) * 100) : 0
  })
}

function hasOpenTradeEquity(buckets: TradeSummary[]): boolean {
  return (
    buckets.length > 0 &&
    buckets.every(
      (bucket) =>
        bucket.netEquity != null &&
        bucket.equity != null &&
        bucket.equityHigh != null &&
        bucket.maxDrawdownPercentage != null
    )
  )
}

// Adds netEquity when the server sent open-trade equity for every bucket; otherwise the data is unchanged.
function withNetEquity({ startingEquity, data }: GroupData): TradeSummary[] {
  if (
    typeof startingEquity !== 'number' ||
    !data.every((bucket) => typeof bucket.equity === 'number')
  ) {
    return data
  }
  return data.map((bucket) => ({
    ...bucket,
    netEquity: (bucket.equity as number) - startingEquity,
  }))
}

function getHighlightColor(series: ChartDataSeries | undefined, theme: Theme): string {
  const lastValue = series?.data[series.data.length - 1] ?? 0
  return lastValue < 0 ? theme.palette.error.main : theme.palette.primary.main
}

// Peers fade back; the highlighted ratio is drawn last so it sits on top.
function styleForHighlight(
  series: ChartDataSeries[],
  highlighted: ChartDataSeries | undefined,
  highlightColor: string
): ChartDataSeries[] {
  const peers = series
    .filter((s) => s !== highlighted)
    .map((s) => ({ ...s, color: FADED_SERIES_COLOR, disableHighlight: true, area: false }))
  if (!highlighted) return peers
  return [...peers, { ...highlighted, color: highlightColor, area: true, disableHighlight: false }]
}

const FADED_SERIES_COLOR = 'rgba(255, 255, 255, 0.14)'

function DrawdownStrip({ id, data, dates }: { id: string; data: number[]; dates: Date[] }) {
  const theme = useTheme()
  if (!data.length || data.length !== dates.length) return null

  return (
    <Box
      sx={{
        position: 'relative',
        height: 44,
        flexShrink: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          left: 8,
          top: 4,
          fontSize: 10,
          fontWeight: 600,
          color: 'text.secondary',
        }}
      >
        DD
      </Typography>
      <LineChart
        skipAnimation
        height={44}
        xAxis={[
          {
            scaleType: 'time',
            data: dates,
            // Same bounds as the equity chart so the two plots line up.
            min: dates[0],
            max: dates[dates.length - 1],
            valueFormatter: (value: Date) => formatDate(value),
          },
        ]}
        yAxis={[{ max: 0 }]}
        leftAxis={null}
        bottomAxis={null}
        axisHighlight={{ x: 'line', y: 'none' }}
        series={[
          {
            id,
            data,
            label: 'Drawdown',
            valueFormatter: (value: number | null) => formatPercentage(value),
            type: 'line',
            area: true,
            showMark: false,
            color: theme.palette.error.main,
          },
        ]}
        margin={{ top: 3, right: 10, left: 44, bottom: 3 }}
        slotProps={{ legend: { hidden: true } }}
        sx={{
          '& .MuiAreaElement-root': { fillOpacity: 0.3 },
          '& .MuiLineElement-root': { strokeWidth: 1 },
        }}
      />
    </Box>
  )
}

function toDay(value: Date | string | number): number {
  const date = new Date(value)
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

// Every bucket date across the loaded groups, led by the day before the first one so curves start at 0.
function buildDateAxis(groupsData: TradeSummary[][]): Date[] {
  const bucketDays = groupsData.flatMap((data) => data.map(({ range }) => toDay(range)))
  const firstDay = bucketDays.length ? Math.min(...bucketDays) : toDay(Date.now())
  const dayBefore = new Date(firstDay)
  dayBefore.setDate(dayBefore.getDate() - 1)
  return Array.from(new Set([dayBefore.getTime(), ...bucketDays]))
    .sort((a, b) => a - b)
    .map((timestamp) => new Date(timestamp))
}

function buildSeriesByGroup(
  groupProfits: Record<string, TradeSummary[]>,
  dates: Date[],
  { idPrefix, accountEquity }: { idPrefix: string; accountEquity: number }
): Record<string, ChartDataSeries> {
  const seriesByGroup: Record<string, ChartDataSeries> = {}
  for (const [group, data] of Object.entries(groupProfits)) {
    const dataPoints = alignChartData(data, dates)
    const series: ChartDataSeries = {
      id: `${idPrefix}-series-${group}`,
      data: dataPoints.map((point) => point.y),
      valueFormatter: (value: number | null) => {
        if (value == null) return ''
        const percentProfit = (value / accountEquity) * 100
        const decimalsToShow = percentProfit < 1 ? 2 : 0
        return `$${value.toFixed(2)} (${percentProfit.toFixed(decimalsToShow)}%)`
      },
      label: group.replace('-', '/'),
      type: 'line',
      showMark: false,
    }

    if ((dataPoints[dataPoints.length - 1]?.y ?? 0) < 0) {
      series.color = `rgba(255, 255, 255, 0.1)`
      series.disableHighlight = true
    }

    seriesByGroup[group] = series
  }
  return seriesByGroup
}

// Align and combine the cumulative profit from daily summaries across all dates
const alignChartData = (dailySummaries: TradeSummary[], allDates: Date[]): ChartDataPoint[] => {
  if (hasOpenTradeEquity(dailySummaries)) {
    return alignNetEquity(dailySummaries, allDates)
  }

  const dataPoints: ChartDataPoint[] = []
  let cumulativeProfit = 0

  const profitByDate = new Map<number, number>()
  dailySummaries.forEach((summary) => {
    const summaryDate = new Date(summary.range)
    const normalizedDate = new Date(
      summaryDate.getFullYear(),
      summaryDate.getMonth(),
      summaryDate.getDate()
    )
    profitByDate.set(normalizedDate.getTime(), summary.profit)
  })

  allDates.forEach((date) => {
    const timestamp = date.getTime()
    if (profitByDate.has(timestamp)) {
      cumulativeProfit += profitByDate.get(timestamp) || 0
    }
    dataPoints.push({
      x: date,
      y: cumulativeProfit,
    })
  })

  return dataPoints
}

// Net profit including open trades at each bucket's end, carried across dates this group has no bucket for.
function alignNetEquity(summaries: TradeSummary[], allDates: Date[]): ChartDataPoint[] {
  const netEquityByDay = new Map(
    summaries.map((summary) => [toDay(summary.range), summary.netEquity as number])
  )
  let netEquity = 0
  return allDates.map((date) => {
    netEquity = netEquityByDay.get(date.getTime()) ?? netEquity
    return { x: date, y: netEquity }
  })
}

function formatDate(dateValue: Date | string | number | null) {
  if (dateValue == null) return ''
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue)
  return date.toLocaleDateString()
}

const RenderAxisContent = (
  props: ChartsAxisContentProps & { drawdown?: number[]; drawdownLabel?: string }
) => {
  const { series, dataIndex, axisValue, drawdown, drawdownLabel } = props
  const drawdownAtIndex =
    drawdown && typeof dataIndex === 'number' ? drawdown[dataIndex] : undefined

  if (dataIndex == null || !series || series.length === 0) {
    return null
  }

  // Extract hovered values for each series
  const seriesWithValues = series
    .map((s) => {
      const seriesObject = s as unknown as ChartSeriesDefaultized<'line'>
      const value =
        typeof dataIndex === 'number' && s.data[dataIndex] != null ? s.data[dataIndex] : null
      return {
        label: seriesObject.label,
        color: seriesObject.color,
        value: value as any,
        valueFormatter: seriesObject.valueFormatter,
      }
    })
    .filter((s) => s.value !== null && s.value !== 0)

  if (seriesWithValues.length === 0) return null

  // Sort the series by the hovered value descending
  const topSeries = seriesWithValues.sort((a, b) => b.value! - a.value!)
  const topTen = topSeries.slice(0, 10)

  return (
    <Box p={1} sx={{ backgroundColor: 'rgba(0, 0, 0, 1)', borderRadius: '4px', color: '#fff' }}>
      <Typography variant="body1" color={'textSecondary'} sx={{ mb: 1 }}>
        Net profit {formatDate(axisValue)} {topSeries.length > 10 ? '(Top 10)' : ''}
      </Typography>

      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', mb: 1 }} />

      {topTen.map((item, index) => {
        const formatedLabel = item.valueFormatter
          ? item.valueFormatter(item.value, { dataIndex })
          : ''

        return (
          <Box
            key={index}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={0.5}
          >
            <Box display="flex" alignItems="center">
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: item.color,
                  display: 'inline-block',
                  mr: 1,
                }}
              />
              <Typography variant="body1" sx={{ color: '#fff' }}>
                {item.label
                  ? typeof item.label === 'string'
                    ? item.label
                    : item.label('tooltip')
                  : ''}
                :
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: '#fff', ml: 2, whiteSpace: 'nowrap' }}>
              {formatedLabel}
            </Typography>
          </Box>
        )
      })}

      {drawdownAtIndex != null && (
        <>
          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', my: 1 }} />
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="body1" color="textSecondary">
              Drawdown{drawdownLabel ? ` (${drawdownLabel})` : ''}:
            </Typography>
            <Typography variant="body1" sx={{ color: 'error.light', ml: 2, whiteSpace: 'nowrap' }}>
              {formatPercentage(drawdownAtIndex)}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  )
}

export default BacktestEquityChart
